import { useTheme } from "@emotion/react";
import { Email, Lock, Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Typography
} from "@mui/material";
import { useState } from "react";
import { ApplicationButton } from "../Components";
import { useMicrospecThemeLanguageContext } from "../Themes";

const LoginEmailPassword = ({
  identifier,
  password,
  error,
  loading,
  rememberMe,
  onPasswordChange,
  onRememberMeChange,
  onSubmit,
  onBackToIdentifier
}) => {
  const { localizationProvider } = useMicrospecThemeLanguageContext();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [showPassword, setShowPassword] = useState(false);
  
  // Generate gradients from theme colors
  const primaryGradient = `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`;

  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleMouseDownPassword = (event) => event.preventDefault();

  return (
    <Box component="form" onSubmit={onSubmit} noValidate>
      {/* Disabled Email Field */}
      <TextField
        margin="normal"
        fullWidth
        id="identifier"
        name="identifier"
        label={localizationProvider.translateByType({
          type: "Internal", 
          text: "emailAddress"
        }) || "البريد الإلكتروني"}
        type="email"
        autoComplete="email"
        dir="rtl"
        variant="outlined"
        value={identifier}
        disabled
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Email sx={{ color: 'action.disabled' }} />
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 3,
            backgroundColor: 'rgba(0,0,0,0.02)',
            '&.Mui-disabled': {
              '& fieldset': {
                borderColor: 'action.disabled',
              },
            },
          },
        }}
      />

      {/* Password Input */}
      <TextField
        margin="normal"
        required
        fullWidth
        id="password"
        name="password"
        label={localizationProvider.translateByType({
          type: "Internal",
          text: "password"
        })}
        type={showPassword ? 'text' : 'password'}
        autoComplete="current-password"
        autoFocus
        dir="rtl"
        variant="outlined"
        value={password}
        onChange={(e) => onPasswordChange(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Lock sx={{ color: 'action.active' }} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleClickShowPassword}
                onMouseDown={handleMouseDownPassword}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      {/* Remember Me and Forgot Password */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 2,
        mt: 1
      }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={rememberMe}
              onChange={(e) => onRememberMeChange(e.target.checked)}
            />
          }
          label={
            <Typography variant="body2">
              {localizationProvider.translateByType({
                type: "Internal",
                text: "rememberMe"
              })}
            </Typography>
          }
        />
        <Link
          href="#"
          variant="body2"
          sx={{
            color: isDarkMode ? '#f0f0f0' : theme.palette.primary.main,
            textDecoration: 'none',
            fontWeight: 500,
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
        >
          {localizationProvider.translateByType({
            type: "Internal",
            text: "forgotPassword"
          })}
        </Link>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Login Button */}
      <ApplicationButton
        type="submit"
        fullWidth
        variant="contained"
        disabled={loading}
        sx={{
          mt: 2,
          mb: 2,
          py: 1.8,
          borderRadius: 3,
          fontSize: '1rem',
          fontWeight: 700,
          textTransform: 'none',
          background: primaryGradient,
          boxShadow: `0 8px 32px ${theme.palette.primary.main}30`,
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 12px 40px ${theme.palette.primary.main}40`,
            background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
          },
          '&:active': {
            transform: 'translateY(0)',
          },
          '&:disabled': {
            background: 'action.disabledBackground',
            color: 'action.disabled',
            transform: 'none',
            boxShadow: 'none',
          },
        }}
      >
        {loading 
          ? (localizationProvider.translateByType({
              type: "Internal",
              text: "signingIn"
            }))
          : (localizationProvider.translateByType({
              type: "Internal",
              text: "signIn"
            }))
        }
      </ApplicationButton>

      {/* Back to Identifier Link */}
      <Box sx={{ textAlign: 'center', mt: 2 }}>
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={onBackToIdentifier}
          sx={{
            color: isDarkMode ? '#f0f0f0' : theme.palette.primary.main,
            textDecoration: 'none',
            fontWeight: 500,
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
        >
          {localizationProvider.translateByType({
            type: "Internal",
            text: "backToEmailSelection"
          })}
        </Link>
      </Box>
    </Box>
  );
};

export default LoginEmailPassword;